/**
 * The schedule table's unsaved edits, worked out separately from the page so the rules
 * (what counts as a change, what is invalid, what gets created) are testable and the
 * page stays about rendering. Nothing here talks to the server.
 */

/** The table's deliberately narrow field set; everything else keeps its schema default. */
export interface DraftFields {
  title: string;
  speaker: string;
  category: string;
  durationMin: string;
}

export interface NewRow extends DraftFields {
  /** Local only — a stable React key before the row has a server id. */
  readonly key: string;
}

/** The slice of a saved item this logic compares against. */
export interface SavedItem {
  readonly id: string;
  readonly title: string;
  readonly speaker: string | null;
  readonly category: string | null;
  readonly durationMs: number;
}

export interface PatchOp {
  readonly id: string;
  readonly body: Record<string, unknown>;
}

export interface CreateOp {
  readonly key: string;
  readonly body: Record<string, unknown>;
}

export interface DraftChanges {
  readonly patches: readonly PatchOp[];
  readonly creates: readonly CreateOp[];
  /** Plain-language reasons the draft can't be saved yet (e.g. a new row with no title). */
  readonly problems: readonly string[];
}

function parseMinutes(text: string): number | null {
  const value = Number.parseFloat(text);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function isBlankRow(row: DraftFields): boolean {
  return (
    !row.title.trim() && !row.speaker.trim() && !row.category.trim() && !row.durationMin.trim()
  );
}

/** Turns the page's edit state into the exact requests a Save would make. */
export function computeDraftChanges(
  items: readonly SavedItem[],
  edits: Readonly<Record<string, Partial<DraftFields>>>,
  newRows: readonly NewRow[],
): DraftChanges {
  const patches: PatchOp[] = [];
  const creates: CreateOp[] = [];
  const problems: string[] = [];

  for (const item of items) {
    const edit = edits[item.id];
    if (!edit) continue;
    const body: Record<string, unknown> = {};

    if (edit.title !== undefined) {
      const title = edit.title.trim();
      if (!title) problems.push("An existing row has no title.");
      else if (title !== item.title) body.title = title;
    }
    if (edit.speaker !== undefined) {
      const speaker = edit.speaker.trim() || null;
      if (speaker !== item.speaker) body.speaker = speaker;
    }
    if (edit.category !== undefined) {
      const category = edit.category.trim() || null;
      if (category !== item.category) body.category = category;
    }
    if (edit.durationMin !== undefined) {
      const minutes = parseMinutes(edit.durationMin);
      if (minutes === null) problems.push("Minutes must be a number above 0.");
      else if (Math.round(minutes * 60_000) !== item.durationMs) {
        body.durationMs = Math.round(minutes * 60_000);
      }
    }

    if (Object.keys(body).length > 0) patches.push({ id: item.id, body });
  }

  for (const row of newRows) {
    if (isBlankRow(row)) continue; // an empty row someone added and never used
    const title = row.title.trim();
    if (!title) {
      problems.push("Every new row needs a title.");
      continue;
    }
    const minutes = row.durationMin.trim() ? parseMinutes(row.durationMin) : undefined;
    if (minutes === null) {
      problems.push(`"${title}": minutes must be a number above 0.`);
      continue;
    }
    creates.push({
      key: row.key,
      body: {
        title,
        speaker: row.speaker.trim() || null,
        category: row.category.trim() || null,
        durationMs: minutes === undefined ? undefined : Math.round(minutes * 60_000),
      },
    });
  }

  return { patches, creates, problems: [...new Set(problems)] };
}

/** How many separate changes a Save would make (for the button label). */
export function changeCount(changes: DraftChanges): number {
  return changes.patches.length + changes.creates.length;
}
